import React from 'react'
import { Field, Form, Formik } from 'formik'
import _isEmpty from 'lodash/isEmpty'

import Button from '../Button'
import { CustomToggleField, SelectField, SimpleField } from '../../formik/Fields'
import { requiredValidator } from '../../../_common/core/validation'
import CustomFieldOptions from './CustomFieldOptions'
import { validateForm } from './utils/formValidation'
import { FIELDS, fieldTypeOptions, toggles } from './utils/constants'

const CustomFieldForm = props => {
  const { fieldData, customFieldsData, onSubmit, onClose } = props
  const isEditing = !_isEmpty(fieldData)


  return (
    <Formik
      initialValues={{
        parentType: 'order',
        type: 'text',
        label: '',
        name: '',
        order: '',
        defaultValue: '',
        required: false,
        settings: {
          showOnMyTickets: false,
          showOnPdf: false,
          showOnScan: false,
          allowUpdate: false,
        },
        selectOptions: [],
        multiSelectOptions: [],
        radioOptions: [],
        ...fieldData,
      }}
      onSubmit={values => {
        onSubmit(values)
      }}
      validateOnMount={false}
      validate={valuesData => validateForm(valuesData, customFieldsData)}
    >
      {({ values, errors, handleSubmit, setFieldValue }) => (
        <Form>
          <div className="row" style={{}}>
            <div className="col-xs-12">
              <Field
                name="parentType"
                label="Append to"
                component={SelectField}
                options={[
                  { value: 'order', label: 'Order' },
                  { value: 'ticket', label: 'Ticket' },
                  { value: 'add-on', label: 'Add-On' },
                ]}
                validate={requiredValidator()}
                disabled={isEditing}
                onChange={(_, value) => {
                  const newParent = value;
                  setFieldValue('parentType', newParent);

                  const allowedTypes =
                    newParent === 'order'
                      ? [...fieldTypeOptions, { value: 'image', label: 'Image' }]
                      : fieldTypeOptions;

                  const isValid = allowedTypes.some(t => t.value === values.type);

                  if (!isValid) {
                    setFieldValue('type', allowedTypes[0].value);
                  }
                }}
              />
            </div>
          </div>
          <div className="row" style={{}}>
            <div className="col-xs-12 col-sm-6">
              <Field
                name="type"
                label="Type"
                component={SelectField}
                options={
                  values.parentType === 'order'
                    ? [...fieldTypeOptions, { value: 'image', label: 'Image' }]
                    : fieldTypeOptions
                }
                validate={requiredValidator()}
              />

            </div>
            <div className="col-xs-12 col-sm-6">
              <Field name="label" label="Label" component={SimpleField} validate={requiredValidator()} />
            </div>
            {values.type === FIELDS.SELECT.key && (
              <CustomFieldOptions
                values={values}
                errors={errors}
                fieldName={FIELDS.SELECT.optionsKey}
                addBtnLabel="Add Select Option"
                setFieldValue={setFieldValue}
              />
            )}
            {values.type === FIELDS.SELECT_MULTI.key && (
              <CustomFieldOptions
                values={values}
                errors={errors}
                fieldName={FIELDS.SELECT_MULTI.optionsKey}
                addBtnLabel="Add Multi Select Option"
              />
            )}
            {values.type === FIELDS.RADIO.key && (
              <CustomFieldOptions
                values={values}
                errors={errors}
                fieldName={FIELDS.RADIO.optionsKey}
                addBtnLabel="Add Radio Option"
              />
            )}
          </div>
          <div className="row">
            <div className="col-xs-12 col-sm-6">
              <Field
                name="name"
                label="Name"
                component={SimpleField}
                validate={requiredValidator()}
                helperText={`
                  The Name field should not contain numbers or spaces.
                  You can replace spaces with underscore like 'first_name' or 'last_name'.
                `}
              />
            </div>
            {values.type !== FIELDS.IMAGE.key && (
              <div className="col-xs-12 col-sm-6">
                <Field name="defaultValue" label="Default Value" component={SimpleField} />
              </div>
            )}
          </div>
          <div className="row">
            <div className="col-xs-12 col-sm-6">
              <Field
                name="order"
                label="Order of display"
                type="numeric"
                component={SimpleField}
                validate={requiredValidator()}
                helperText={`
                 The order where it will be displayed to the user, 
                 if 2 values have the same order they might not appear in the order expected.
                `}
              />
            </div>
            <div className="col-xs-12 col-sm-6" />
          </div>
          <div className="row">
            {toggles.map(toggleItem => (
              <div key={toggleItem.key} className="col-xs-12 col-sm-6">
                <Field name={toggleItem.key} onText={toggleItem.onText} component={CustomToggleField} />
              </div>
            ))}
          </div>

          {Array.isArray(values.settings.conditions) && values.settings.conditions.length > 0 && (
            <>
              <div className="row">
                <div className="col-xs-12" style={{ paddingTop: '20px', paddingBottom: '20px' }}>
                  Set the condition to show the custom field
                </div>
              </div>
              {values.settings.conditions.map(condition => (
                <div className="row" key={`${condition.field}`}>
                  <div className="col-xs-4">
                    <Field
                      name="settings.conditions[0].field"
                      value={condition.field}
                      label="Field"
                      component={SelectField}
                      options={
                        customFieldsData
                          .filter(f => f.type === values.parentType)
                          .flatMap(f => f.fields)
                          .filter(f => !f.settings?.conditions)
                          .map(f => ({
                            value: f.name,
                            label: `${f.label} (${f.name})`,
                          }))
                      }
                      disabled
                    />
                  </div>
                  <div className="col-xs-4">
                    <Field
                      name="settings.conditions[0].operator"
                      value={condition.operator}
                      label="Operator"
                      component={SelectField}
                      options={[
                        { value: '==', label: '==' },
                        { value: '!=', label: '!=' },
                        { value: '>', label: '>' },
                        { value: '<', label: '<' },
                        { value: '>=', label: '>=' },
                        { value: '<=', label: '<=' },
                      ]}
                      disabled
                    />
                  </div>
                  <div className="col-xs-4">
                    <Field
                      name="settings.conditions[0].value"
                      value={condition.value}
                      label="Value"
                      component={SimpleField}
                      disabled
                    />
                  </div>
                </div>
              ))}
            </>
          )}
          <div className="modal-footer" style={{ margin: '20px -15px 0px' }}>
            <div className="btn-toolbar btn-toolbar-right">
              <Button
                className="btn btn-default btn-success"
                type="button"
                onClick={() => {
                  handleSubmit()
                }}
              >
                {isEditing ? 'Update' : 'Create'}
              </Button>
              <Button className="btn btn-default btn-cancel" type="button" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  )
}

export default CustomFieldForm
